/**
 * Folklory Physical Add-on Handler
 * ================================
 * Keeps hidden physical add-on products (e.g. the cassette speaker) in sync
 * with the Easify option chosen on the main Folklory line item.
 *
 * The main product is digital (requires_shipping = false). The hidden add-on
 * product is physical, and its presence in the cart is what makes Shopify ask
 * for a delivery address, offer shipping rates, and accept a free-shipping code.
 * If the add-on line is missing, the customer is never asked where to send the
 * speaker and Shopify collects a billing address only.
 *
 * Rather than react to one particular request, this reconciles the whole cart
 * after any cart mutation and on page load:
 *   desired add-on quantity = total quantity of main line items whose Easify
 *   property selects that add-on. Anything else is corrected.
 *
 * That covers adding, editing the option after the fact, changing quantity and
 * removing the line, including the /cart/change path used when a customer goes
 * back from checkout to edit their choice.
 *
 * HOW TO ADD A NEW PHYSICAL ADD-ON:
 * 1. Create a hidden physical product in Shopify (set weight, requires shipping)
 * 2. Copy its variant ID from the admin URL
 * 3. Add an entry to PHYSICAL_ADDONS below
 * 4. Create the matching Easify option on the Folklory product
 *
 * Loaded via main-product.liquid on the Folklory product page.
 */
(function () {
  'use strict';

  // The product page loads this via main-product.liquid and theme.liquid loads
  // it site-wide, so without this guard window.fetch gets wrapped twice and each
  // cart change reconciles twice, which can add the add-on twice.
  if (window.__folkloryAddonsLoaded) return;
  window.__folkloryAddonsLoaded = true;

  // ================================================================
  // CONFIGURATION - map Easify option values to hidden product variant IDs
  // ================================================================
  var PHYSICAL_ADDONS = {
    'Cassette Choice': {
      'Cassette Bundle': '52406764667163'
    },
    'Cassette Chioce': {
      'Cassette Bundle': '52406764667163'
    }
    // Future add-ons - just add new entries:
    // 'Vinyl Choice': {
    //   'Add Vinyl Record': 'REPLACE_WITH_VINYL_VARIANT_ID'
    // }
  };

  // ================================================================
  // END CONFIGURATION
  // ================================================================

  var origFetch = window.fetch;
  var reconciling = false;

  // Every variant id that this script manages, so a line holding one can be
  // told apart from a product the customer chose themselves.
  function managedVariantIds() {
    var ids = {};
    for (var optionName in PHYSICAL_ADDONS) {
      if (!PHYSICAL_ADDONS.hasOwnProperty(optionName)) continue;
      var valueMap = PHYSICAL_ADDONS[optionName];
      for (var label in valueMap) {
        if (!valueMap.hasOwnProperty(label)) continue;
        ids[String(valueMap[label])] = true;
      }
    }
    return ids;
  }

  /**
   * Which add-on variant, if any, a cart line item's properties select.
   * Easify may or may not prefix the property name with an underscore, and it
   * appends the price to the value, so the match is a substring one.
   */
  function addonVariantForLineItem(item) {
    var props = item.properties || {};
    for (var optionName in PHYSICAL_ADDONS) {
      if (!PHYSICAL_ADDONS.hasOwnProperty(optionName)) continue;

      var val = props['_' + optionName];
      if (!val) val = props[optionName];
      if (!val) continue;

      var valueMap = PHYSICAL_ADDONS[optionName];
      for (var label in valueMap) {
        if (!valueMap.hasOwnProperty(label)) continue;
        if (String(val).indexOf(label) !== -1) return String(valueMap[label]);
      }
    }
    return null;
  }

  function getCart() {
    return origFetch('/cart.js', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  function changeLine(lineKey, quantity) {
    return origFetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    });
  }

  function addLine(variantId, quantity) {
    return origFetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: quantity }] })
    });
  }

  /**
   * Bring add-on lines in line with what the main line items actually ask for.
   * Returns a promise that resolves once the cart is correct.
   */
  function reconcileAddons() {
    if (reconciling) return Promise.resolve();
    reconciling = true;

    var managed = managedVariantIds();

    return getCart().then(function (cart) {
      var items = (cart && cart.items) || [];
      var wanted = {};   // variantId -> quantity the cart should hold
      var present = {};  // variantId -> { key, quantity } already in the cart

      items.forEach(function (item) {
        var variantId = String(item.variant_id || item.id);

        if (managed[variantId] && !addonVariantForLineItem(item)) {
          // An add-on line. Add-on lines carry no Easify property of their own,
          // which is what separates them from a main line.
          present[variantId] = { key: item.key, quantity: item.quantity };
          return;
        }

        var addonId = addonVariantForLineItem(item);
        if (addonId) wanted[addonId] = (wanted[addonId] || 0) + item.quantity;
      });

      var work = [];

      // Add or correct the quantity of add-ons the cart should hold.
      Object.keys(wanted).forEach(function (variantId) {
        var need = wanted[variantId];
        var have = present[variantId];
        if (!have) {
          work.push(addLine(variantId, need));
        } else if (have.quantity !== need) {
          work.push(changeLine(have.key, need));
        }
      });

      // Drop add-on lines nothing asks for any more, which is what happens when
      // a customer switches back to the digital-only option or removes the item.
      Object.keys(present).forEach(function (variantId) {
        if (!wanted[variantId]) work.push(changeLine(present[variantId].key, 0));
      });

      return work.length ? Promise.all(work) : null;
    }).then(function (result) {
      reconciling = false;
      return result;
    }).catch(function (err) {
      reconciling = false;
      // Never let a reconcile failure break the storefront. The worst case is
      // the cart staying as the customer left it.
      if (window.console && console.warn) {
        console.warn('[folklory-addons] reconcile failed', err);
      }
      return null;
    });
  }

  // Expose for manual checks from the console during testing.
  window.folkloryReconcileAddons = reconcileAddons;

  // ----------------------------------------------------------------
  // Reconcile after any cart mutation, whichever code path caused it.
  // ----------------------------------------------------------------
  var CART_MUTATIONS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

  function isCartMutation(url) {
    if (typeof url !== 'string') return false;
    for (var i = 0; i < CART_MUTATIONS.length; i++) {
      if (url.indexOf(CART_MUTATIONS[i]) !== -1) return true;
    }
    return false;
  }

  window.fetch = function (url) {
    var args = arguments;

    if (reconciling || !isCartMutation(url)) {
      return origFetch.apply(this, args);
    }

    var self = this;
    return origFetch.apply(self, args).then(function (response) {
      if (!response.ok) return response;

      // The theme reads this response to render the cart drawer, so hand back a
      // clone and let the reconcile run behind it.
      var cloned = response.clone();
      reconcileAddons();
      return cloned;
    });
  };

  // ----------------------------------------------------------------
  // Also reconcile on load, which catches carts left inconsistent by an
  // earlier session or by a path this script never saw.
  // ----------------------------------------------------------------
  function reconcileOnLoad() {
    if (window.location.pathname.indexOf('/cart') === 0 ||
        document.querySelector('form[action*="/cart"]')) {
      reconcileAddons();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileOnLoad);
  } else {
    reconcileOnLoad();
  }

  // Back/forward navigation restores a cached page, so check again.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) reconcileAddons();
  });
})();
